// DEPENDENCIES
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
// COMPONENTS
import MUIDataTable from 'mui-datatables';
import { Button, Modal, Form } from 'react-bootstrap';
import SVG from 'react-inlinesvg';
// CUSTOM COMPONENTS
import * as alert from '../../../redux/alertSnackBarRedux';
import * as confirmModal from '../../../redux/confirmModalRedux';
// SERVICES AND HELPERS
import * as intervalTypeService from '../../../services/companyData/intervalTypeService';
import TrainingTypeValidator from '../../../helpers/validators/TrainingTypeValidator';
import { getInputClasses } from '../../../helpers/helper';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';

var initialIntervalType = {
  id: 0,
  name: '',
  description: '',
};

const IntervalTypesListingPage = (props) => {
  const { showAlert, showConfirmModal, hideConfirmModal } = props;
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIntervalType, setSelectedIntervalType] = useState(initialIntervalType);

  useEffect(() => {
    loadTableData();
  }, []);

  const loadTableData = () => {
    intervalTypeService
      .getIntervalTypes()
      .then((res) => {
        setData(res);
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      });
  };

  const editIntervalType = (id) => {
    intervalTypeService.getIntervalTypeById(id).then((res) => {
      setSelectedIntervalType(res);
      setIsModalOpen(true);
    });
  };

  const deleteIntervalType = (id) => {
    intervalTypeService.deleteIntervalType(id).then((res) => {
      showAlert({ message: res.message, variant: 'success' });
      loadTableData();
      hideConfirmModal();
    });
  };

  const columns = [
    {
      name: 'id',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        width: 1,
        print: false,
        download: false,
        setCellProps: () => ({ 'data-aes-field': 'actions' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'actions' }),
        customBodyRenderLite: (dataIndex) => {
          return (
            <div className='aes-admin-listing-actions'>
              <button
                className='btn btn-sm btn-info font-weight-bold py-2 px-3 px-xxl-5'
                onClick={() => editIntervalType(data[dataIndex].id)}
              >
                Edit
              </button>
              <button
                onClick={() =>
                  showConfirmModal({
                    title: 'Delete Interval Type',
                    text: 'Are you sure you want to delete this interval type?',
                    rightBtnText: 'Confirm',
                    btnAction: () => deleteIntervalType(data[dataIndex].id),
                  })
                }
                className='btn btn-sm btn-danger font-weight-bold py-2 px-3 px-xxl-5 my-1'
              >
                Delete
              </button>
            </div>
          );
        },
      },
    },
    {
      name: 'name',
      label: 'Name',
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'primary', 'data-aes-label': 'Name' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'primary' }),
      },
    },
    {
      name: 'description',
      label: 'Description',
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ 'data-aes-field': 'detail', 'data-aes-label': 'Description' }),
        setCellHeaderProps: () => ({ 'data-aes-field': 'detail' }),
        customBodyRenderLite: (dataIndex) => {
          return <>{data[dataIndex].description ? data[dataIndex].description : '-'}</>;
        },
      },
    },
  ];

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedIntervalType(initialIntervalType);
    formik.resetForm();
  };

  const saveIntervalType = (values, setStatus, setSubmitting) => {
    intervalTypeService
      .saveIntervalType(values)
      .then((res) => {
        showAlert({ message: res.message, variant: 'success' });
        loadTableData();
        handleModalClose();
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: selectedIntervalType,
    validationSchema: TrainingTypeValidator,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      saveIntervalType(values, setStatus, setSubmitting);
    },
  });

  return (
    <div className='row aes-admin-listing-page aes-admin-listing-page--interval-types'>
      <Modal centered size='lg' show={isModalOpen} onHide={handleModalClose}>
        <form className='card card-custom' onSubmit={formik.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Interval Type</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='form'>
              <div className='form-group row'>
                <label className='col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600'>Name</label>
                <div className='col-lg-9 col-xl-9'>
                  <div className='input-group input-group-lg input-group-solid'>
                    <input
                      mask='999-99-9999'
                      placeholder='Start typing...'
                      className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, 'name')}`}
                      name='name'
                      {...formik.getFieldProps('name')}
                    />
                  </div>
                  {formik.touched.name && formik.errors.name ? (
                    <div className='invalid-feedback display-block'>{formik.errors.name}</div>
                  ) : null}
                </div>
              </div>
              <div className='form-group row'>
                <label className='col-xl-3 col-lg-3 col-form-label col-xl-3 col-lg-3 col-form-label fz-mdsm fw-600'>Description</label>
                <div className='col-lg-9 col-xl-9'>
                  <div className='input-group input-group-lg input-group-solid'>
                    <Form.Control
                      as='textarea'
                      rows='5'
                      placeholder='Start typing...'
                      className={`form-control form-control-lg form-control-solid ${getInputClasses(formik, 'description')}`}
                      name='description'
                      {...formik.getFieldProps('description')}
                    />
                  </div>
                  {formik.touched.description && formik.errors.description ? (
                    <div className='invalid-feedback display-block'>{formik.errors.description}</div>
                  ) : null}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={handleModalClose}>
              Close
            </Button>
            <button type='submit' className='btn btn-success mr-2' disabled={formik.isSubmitting || (formik.touched && !formik.isValid)}>
              Save
              {formik.isSubmitting}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
      <div className='col-lg-12 col-xxl-12'>
        <div className={`card card-custom card-stretch gutter-b}`}>
          <div className='card-header border-0 py-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label font-weight-bolder text-dark'>Interval Types</span>
              <span className='text-muted mt-3 font-weight-bold fz-xs'>View and Manage Interval Types</span>
            </h3>
            <div className='card-toolbar'>
              <button onClick={() => setIsModalOpen(true)} className='btn btn-success font-weight-bolder font-size-sm mr-3'>
                <span className='svg-icon svg-icon-lg svg-icon-white'>
                  <SVG src={toAbsoluteUrl('/media/svg/icons/Code/Plus.svg')} />
                </span>{' '}
                Add Interval Type
              </button>
            </div>
          </div>
          <div className='container-fluid aes-admin-listing p-0'>
            <MUIDataTable
              className='aes-admin-listing-surface'
              data={data}
              columns={columns}
              options={{
                responsive: 'standard',
                rowsPerPage: 25,
                rowsPerPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50, 100],
                selectableRows: 'none',
                setTableProps: () => ({
                  className: 'aes-admin-listing-table',
                }),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(null, { ...alert.actions, ...confirmModal.actions })(IntervalTypesListingPage);
